import React, { useEffect, useRef, useState } from "react"
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Add, AddPhotoAlternate, Close, Delete, Image } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import { CadastroBannerFornecedorActionsTypes } from "../context/cadasBannerFornecedorActions"
import { useCadasBannerFornecedorContext } from "../context/useCadasBannerFornecedorContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    uploadArea: {
        border: "2px dashed #c0c0c0",
        borderRadius: 12,
        padding: theme.spacing(3),
        textAlign: "center",
        cursor: "pointer",
        marginBottom: theme.spacing(2),
        "&:hover": { borderColor: "#4caf50", backgroundColor: "#f9fff9" },
    },
    previewImg: {
        maxHeight: 150,
        maxWidth: "100%",
        borderRadius: 8,
        marginBottom: theme.spacing(1),
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        padding: theme.spacing(1, 4),
        boxShadow: "0 4px 12px rgba(76,175,80,0.3)",
        "&:hover": { backgroundColor: "#388e3c" },
    },
    bannerThumb: {
        width: 60,
        height: 40,
        objectFit: "cover",
        borderRadius: 4,
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
}))

export function BannerForm() {
    const classes = useStyles()
    const { dados, dispatch, salvar, excluir, upload, erroCarregamento } =
        useCadasBannerFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()
    const inputRef = useRef<HTMLInputElement>(null)
    const [dialogAberto, setDialogAberto] = useState(false)

    const ehVideo = (url?: string) => !!url && /\.mp4(\?|#|$)/i.test(url)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const handleArquivoSelecionado = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = e.target.files?.[0]
        if (!arquivo) return
        try {
            await upload(arquivo)
            alertStart("Imagem carregada com sucesso!", "success")
        } catch (err) {
            alertStart((err as Error).message || "Erro ao fazer upload da imagem", "error")
        }
        if (inputRef.current) inputRef.current.value = ""
    }

    const handleSalvar = async () => {
        try {
            await salvar()
            alertStart("Banner salvo com sucesso!", "success")
            setDialogAberto(false)
        } catch (err) {
            alertStart((err as Error).message || "Erro ao salvar banner", "error")
        }
    }

    const handleExcluir = async (id: number) => {
        try {
            await excluir(id)
            alertStart("Banner removido com sucesso!", "success")
        } catch (err) {
            alertStart((err as Error).message || "Erro ao remover banner", "error")
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box
                    mb={3}
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    style={{ gap: 12 }}
                >
                    <Box flexGrow={1}>
                        <Typography variant="h5" className={classes.titleText}>
                            Cadastro de Banner
                        </Typography>
                        <Typography variant="body2" style={{ color: "#7f8c8d", marginTop: 4 }}>
                            Aqui você cadastra os banners do seu aplicativo.
                        </Typography>
                    </Box>
                    <Button
                        className={classes.saveButton}
                        startIcon={<Add />}
                        onClick={() => setDialogAberto(true)}
                    >
                        Adicionar Banner
                    </Button>
                </Box>

                {/* Card — lista de banners */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Image className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Banners Cadastrados
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.banners.length === 0 ? (
                            <Typography
                                variant="body2"
                                style={{ color: "#7f8c8d", textAlign: "center", padding: 16 }}
                            >
                                Nenhum banner cadastrado.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table
                                    size="small"
                                    stickyHeader
                                    style={{ width: "100%", minWidth: 380 }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 80 }}>Mídia</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>Cardápio</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>Tela Inicial</TableCell>
                                            <TableCell style={{ minWidth: 60 }} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.banners.map(banner => (
                                            <TableRow key={banner.id}>
                                                <TableCell>
                                                    {ehVideo(banner.urlBanner) ? (
                                                        <video
                                                            src={banner.urlBanner}
                                                            className={classes.bannerThumb}
                                                            muted
                                                            loop
                                                            playsInline
                                                            controls
                                                        />
                                                    ) : (
                                                        <img
                                                            src={banner.urlBanner}
                                                            alt="Banner"
                                                            className={classes.bannerThumb}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {banner.exibirCardapio ? "Sim" : "Não"}
                                                </TableCell>
                                                <TableCell>
                                                    {banner.exibirTelaInicial ? "Sim" : "Não"}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleExcluir(banner.id)}
                                                        title="Excluir banner"
                                                    >
                                                        <Delete style={{ color: "#e74c3c" }} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* Popup — novo banner */}
            <Dialog
                open={dialogAberto}
                onClose={() => setDialogAberto(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle disableTypography>
                    <Box display="flex" alignItems="center">
                        <Image className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle} style={{ flexGrow: 1 }}>
                            Adicionar Banner
                        </Typography>
                        <IconButton size="small" onClick={() => setDialogAberto(false)}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*,video/mp4"
                        style={{ display: "none" }}
                        onChange={handleArquivoSelecionado}
                    />

                    <Box className={classes.uploadArea} onClick={() => inputRef.current?.click()}>
                        {dados.novoUrlBanner ? (
                            ehVideo(dados.novoUrlBanner) ? (
                                <video
                                    src={dados.novoUrlBanner}
                                    className={classes.previewImg}
                                    muted
                                    loop
                                    playsInline
                                    controls
                                />
                            ) : (
                                <img
                                    src={dados.novoUrlBanner}
                                    alt="Preview do banner"
                                    className={classes.previewImg}
                                />
                            )
                        ) : (
                            <>
                                <AddPhotoAlternate
                                    style={{ fontSize: 48, color: "#b0b0b0", marginBottom: 8 }}
                                />
                                <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                    Clique para selecionar a imagem ou vídeo do banner
                                </Typography>
                                <Typography variant="caption" style={{ color: "#b0b0b0" }}>
                                    JPG, PNG, GIF, WEBP, MP4 — até 5 MB
                                </Typography>
                            </>
                        )}
                    </Box>

                    <Box display="flex" flexWrap="wrap" style={{ gap: 16, marginBottom: 16 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={dados.novoExibirCardapio}
                                    onChange={e =>
                                        dispatch({
                                            type: CadastroBannerFornecedorActionsTypes.SET_EXIBIR_CARDAPIO,
                                            payload: e.target.checked,
                                        })
                                    }
                                    style={{ color: "#4caf50" }}
                                />
                            }
                            label="Exibir no Cardápio"
                        />
                        {dados.podeCriarBannerTelaInicial && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={dados.novoExibirTelaInicial}
                                        onChange={e =>
                                            dispatch({
                                                type: CadastroBannerFornecedorActionsTypes.SET_EXIBIR_TELA_INICIAL,
                                                payload: e.target.checked,
                                            })
                                        }
                                        style={{ color: "#4caf50" }}
                                    />
                                }
                                label="Exibir na Tela Inicial"
                            />
                        )}
                    </Box>

                    <Box display="flex" justifyContent="center" pb={2}>
                        <Button
                            className={classes.saveButton}
                            onClick={handleSalvar}
                            disabled={!dados.novoUrlBanner}
                        >
                            Salvar Banner
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    )
}
